import { z } from "zod";

import {
  boundedTextSchema,
  kebabCaseIdentifierSchema,
  localOggAudioReferenceSchema,
  localResourceReferenceSchema
} from "@/domain/schemas";

const draggableEntitySchema = z.strictObject({
  id: kebabCaseIdentifierSchema,
  label: boundedTextSchema(80),
  asset: localResourceReferenceSchema.optional(),
  audio: localOggAudioReferenceSchema.optional()
});

export const dragOrganizeItemSchema = draggableEntitySchema;
export const dragOrganizeDropZoneSchema = draggableEntitySchema;

export const dragOrganizePlacementSchema = z.strictObject({
  itemId: kebabCaseIdentifierSchema,
  dropZoneId: kebabCaseIdentifierSchema
});

export const dragOrganizePayloadSchema = z
  .strictObject({
    items: z.array(dragOrganizeItemSchema).min(1),
    dropZones: z.array(dragOrganizeDropZoneSchema).min(1),
    correctPlacements: z.array(dragOrganizePlacementSchema).min(1)
  })
  .superRefine((payload, context) => {
    const itemIds = new Set<string>();
    const dropZoneIds = new Set<string>();
    const placedItemIds = new Set<string>();

    payload.items.forEach((item, index) => {
      if (itemIds.has(item.id)) {
        context.addIssue({
          code: "custom",
          path: ["items", index, "id"],
          message: "Item identifiers must be unique"
        });
      }

      itemIds.add(item.id);
    });

    payload.dropZones.forEach((dropZone, index) => {
      if (dropZoneIds.has(dropZone.id)) {
        context.addIssue({
          code: "custom",
          path: ["dropZones", index, "id"],
          message: "Drop zone identifiers must be unique"
        });
      }

      dropZoneIds.add(dropZone.id);
    });

    payload.correctPlacements.forEach((placement, index) => {
      if (!itemIds.has(placement.itemId)) {
        context.addIssue({
          code: "custom",
          path: ["correctPlacements", index, "itemId"],
          message: "Correct placements must reference a known item"
        });
      }

      if (!dropZoneIds.has(placement.dropZoneId)) {
        context.addIssue({
          code: "custom",
          path: ["correctPlacements", index, "dropZoneId"],
          message: "Correct placements must reference a known drop zone"
        });
      }

      if (placedItemIds.has(placement.itemId)) {
        context.addIssue({
          code: "custom",
          path: ["correctPlacements", index, "itemId"],
          message: "Each item must have exactly one correct placement"
        });
      }

      placedItemIds.add(placement.itemId);
    });

    payload.items.forEach((item) => {
      if (!placedItemIds.has(item.id)) {
        context.addIssue({
          code: "custom",
          path: ["correctPlacements"],
          message: "Missing a correct placement for item " + item.id
        });
      }
    });
  });

export type DragOrganizeItem = z.infer<typeof dragOrganizeItemSchema>;
export type DragOrganizeDropZone = z.infer<
  typeof dragOrganizeDropZoneSchema
>;
export type DragOrganizePlacement = z.infer<
  typeof dragOrganizePlacementSchema
>;
export type DragOrganizePayload = z.infer<typeof dragOrganizePayloadSchema>;
